export type ScalarAttrType =
  | "string"
  | "html"
  | "stringlist"
  | "link"
  | "linklist"
export type AttrSpec = { type: ScalarAttrType } | { type: "widgetlist" }
export type ClassAttrMap = Record<string, AttrSpec>
export type LocalizableAttributeRegistry = Record<string, ClassAttrMap>

const PAGE_STRING_ATTRS: ClassAttrMap = {
  title: { type: "string" },
  description: { type: "string" },
  navigationTitle: { type: "string" },
  tcTitle: { type: "string" },
  tcDescription: { type: "string" },
  ogTitle: { type: "string" },
  ogDescription: { type: "string" },
  keywords: { type: "stringlist" },
  body: { type: "widgetlist" }
}

export const LOCALIZABLE_ATTRIBUTES: LocalizableAttributeRegistry = {
  // ── Page-type Objs ────────────────────────────────────────────────────────
  Homepage: { ...PAGE_STRING_ATTRS },
  Page: { ...PAGE_STRING_ATTRS },
  LandingPage: { ...PAGE_STRING_ATTRS },
  PortalNewsPage: { ...PAGE_STRING_ATTRS },
  PortalNewsPost: {
    ...PAGE_STRING_ATTRS,
    text: { type: "widgetlist" },
    media: { type: "widgetlist" }
  },
  PortfolioPage: { ...PAGE_STRING_ATTRS },
  BuildingReferencesOverviewPage: { ...PAGE_STRING_ATTRS },
  KpiPage: { ...PAGE_STRING_ATTRS },
  FavoritesPage: { ...PAGE_STRING_ATTRS },
  ProjectsPage: { ...PAGE_STRING_ATTRS },
  ProjectPage: { ...PAGE_STRING_ATTRS },
  ProjectTeamInvitationListPage: { ...PAGE_STRING_ATTRS },
  SharedProjectPage: { ...PAGE_STRING_ATTRS },
  TeamsOverviewPage: { ...PAGE_STRING_ATTRS },
  DimConfigurationAdminPage: { ...PAGE_STRING_ATTRS },
  FollowUpConfigurationAdminPage: { ...PAGE_STRING_ATTRS },
  ProductCategory: { ...PAGE_STRING_ATTRS },
  BuildingReference: {
    ...PAGE_STRING_ATTRS,
    planner: { type: "string" },
    owner: { type: "string" },
    architect: { type: "string" }
  },
  InstallationReference: { ...PAGE_STRING_ATTRS },

  // ── Standalone content Objs ────────────────────────────────────────────────
  Product: {
    ...PAGE_STRING_ATTRS,
    titleShort: { type: "string" },
    descriptionShort: { type: "string" },
    productDescription: { type: "stringlist" }
  },
  ProductBaseVariant: {
    title: { type: "string" },
    titleShort: { type: "string" },
    descriptionShort: { type: "string" },
    description: { type: "stringlist" },
    productDescription: { type: "stringlist" }
  },
  ProductAddon: {
    title: { type: "string" },
    description: { type: "stringlist" }
  },
  ProductGroup: {
    title: { type: "string" }
  },
  AttributeInformation: {
    title: { type: "string" },
    description: { type: "string" }
  },
  AttributeInformationWrapper: {
    title: { type: "string" },
    description: { type: "string" }
  },
  ContactDetails: {
    title: { type: "string" },
    name: { type: "string" },
    street: { type: "string" },
    city: { type: "string" },
    zip: { type: "string" },
    county: { type: "string" },
    country: { type: "string" },
    fax: { type: "string" },
    website: { type: "string" },
    mapsURL: { type: "link" },
    telephone: { type: "link" },
    email: { type: "link" }
  },
  Footer: {
    contactDetailsWidget: { type: "widgetlist" },
    socialsWidget: { type: "widgetlist" },
    topLinks: { type: "widgetlist" },
    bottomLinks: { type: "widgetlist" }
  },
  AppSidebar: {
    menuLinks: { type: "widgetlist" },
    menuLinksSecondary: { type: "widgetlist" },
    menuLinksTertiary: { type: "widgetlist" },
    userDetailsSlot: { type: "widgetlist" }
  },
  Image: {
    title: { type: "string" },
    alternativeText: { type: "string" }
  },
  Download: {
    title: { type: "string" }
  },

  // ── Container widgets (traversal only) ────────────────────────────────────
  ColumnContainerWidget: { columns: { type: "widgetlist" } },
  ColumnWidget: { content: { type: "widgetlist" } },
  CardWidget: { content: { type: "widgetlist" } },
  CenterWidget: { content: { type: "widgetlist" } },
  GridWidget: { items: { type: "widgetlist" } },
  SlideWidget: { content: { type: "widgetlist" } },
  SliderWidget: { slides: { type: "widgetlist" } },
  ButtonGroupWidget: { buttons: { type: "widgetlist" } },
  DataWidget: { content: { type: "widgetlist" } },
  DataGroupWidget: { content: { type: "widgetlist" } },
  DataEmptyWidget: { content: { type: "widgetlist" } },
  DataMessageWidget: { content: { type: "widgetlist" } },
  UserKpiWidget: { content: { type: "widgetlist" } },
  ProjectsKpiWidget: { content: { type: "widgetlist" } },
  RegistrationKpiWidget: { content: { type: "widgetlist" } },
  SignInKpiWidget: { content: { type: "widgetlist" } },
  MyProfileCardHeaderWidget: { content: { type: "widgetlist" } },
  ProjectOpportunityWidget: {
    content: { type: "widgetlist" },
    emptyContent: { type: "widgetlist" }
  },
  AccordionWidget: { tabs: { type: "widgetlist" } },
  BenefitItemWidget: { featureLabel: { type: "widgetlist" } },
  BenefitsWidget: {
    title: { type: "widgetlist" },
    subtitle: { type: "widgetlist" },
    columnHeaders: { type: "widgetlist" },
    items: { type: "widgetlist" }
  },

  // ── Widgets with localizable scalars (and optional widgetlist children) ───
  TextWidget: { text: { type: "html" } },
  MarkdownWidget: { text: { type: "string" } },
  ImageWidget: {
    alternativeText: { type: "string" },
    link: { type: "link" }
  },
  LightDarkImageWidget: {
    alternativeText: { type: "string" },
    link: { type: "link" }
  },
  ButtonWidget: {
    title: { type: "string" },
    target: { type: "link" }
  },
  LoginButtonWidget: { text: { type: "string" } },
  LinkListWidget: {
    title: { type: "string" },
    links: { type: "linklist" },
    iconLinks: { type: "widgetlist" }
  },
  LinkListIconLinkWidget: { link: { type: "link" } },
  LinkListActionWidget: { title: { type: "string" } },
  SocialsWidget: {
    title: { type: "string" },
    linkedin: { type: "link" },
    twitter: { type: "link" },
    youtube: { type: "link" },
    facebook: { type: "link" },
    xing: { type: "link" },
    instagram: { type: "link" }
  },
  TabWidget: {
    title: { type: "string" },
    content: { type: "widgetlist" }
  },
  TestimonialWidget: {
    testimony: { type: "string" },
    testimonyLong: { type: "string" },
    author: { type: "string" },
    authorPosition: { type: "string" },
    authorAffiliation: { type: "string" }
  },
  FeatureWidget: {
    topHeadline: { type: "string" },
    headline: { type: "widgetlist" },
    text: { type: "widgetlist" },
    buttons: { type: "widgetlist" }
  },
  FeatureOverviewWidget: {
    title: { type: "string" },
    subtitle: { type: "string" },
    items: { type: "widgetlist" }
  },
  FeatureOverviewItemWidget: {
    title: { type: "string" },
    subtitle: { type: "string" }
  },
  HeroWidget: {
    headline: { type: "widgetlist" },
    text: { type: "widgetlist" },
    buttons: { type: "widgetlist" }
  },
  AccordionFeatureWidget: {
    headline: { type: "widgetlist" },
    text: { type: "widgetlist" },
    accordion: { type: "widgetlist" },
    cta: { type: "widgetlist" }
  },
  LogoSliderWidget: {
    title: { type: "widgetlist" },
    subtitle: { type: "widgetlist" }
  },
  FinalCallToActionWidget: {
    title: { type: "widgetlist" },
    subtitle: { type: "widgetlist" },
    button: { type: "widgetlist" }
  },
  StorylaneWidget: {
    headline: { type: "widgetlist" },
    text: { type: "widgetlist" }
  },
  DataFormContainerWidget: {
    submittedMessage: { type: "string" },
    content: { type: "widgetlist" }
  },
  DataFormInputFieldWidget: {
    label: { type: "string" },
    placeholder: { type: "string" },
    helpText: { type: "html" }
  },
  DataFormBooleanWidget: {
    label: { type: "string" },
    helpText: { type: "html" }
  },
  DataFormSubmitButtonWidget: {
    submitTitle: { type: "string" },
    cancelTitle: { type: "string" }
  },
  DataLabelWidget: {
    label: { type: "string" },
    details: { type: "string" }
  },
  DataLoadMoreButtonWidget: { title: { type: "string" } },
  PimContentWidget: {
    name: { type: "string" },
    label: { type: "string" },
    text: { type: "html" }
  },
  ProductDeliveryTimeUploadWidget: {
    fileLabel: { type: "string" },
    inputLabel: { type: "string" }
  },
  CreateOpportunityButtonWidget: { text: { type: "string" } },
  UserDetailsWidget: { link: { type: "link" } }
}
